import { useEffect, useRef, useState } from 'react';
import MultiChart from '../../../../../../components/MultiChart';
import { MrcEpisode } from '../../../../../ProfitCalculations/types';
import CandleChart from './CandleChart';
import RsiPanel, { RsiSide } from './RsiPanel';

export interface RsiDescriptor {
  key: string;
  label: string;
  side: RsiSide;
}

interface Props {
  /** Height of the candle (main) chart. MultiChart sizes each RSI panel to
   * mainHeight / 5, so the rendered stack is taller than mainHeight. */
  mainHeight: number;
  txId?: number;
  live?: boolean;
  rsis: RsiDescriptor[];
  /** When set, the candle chart zooms to this MRC episode (deep dive). */
  mrcEpisode?: MrcEpisode;
}

const ChartStack = ({ mainHeight, txId, live, rsis, mrcEpisode }: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  // Deferred (useEffect, not useLayoutEffect) so the surrounding row paints
  // immediately; the heavy Victory mount then happens off the click frame.
  useEffect(() => {
    if (!ref.current) return;
    const element = ref.current;
    const measure = () => setWidth(element.clientWidth);
    measure();
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  // MultiChart stacks the candle (mainHeight) + one indicator per RSI panel
  // (mainHeight / 5 each) + a 2px divider after each child. Reserve that
  // height up front so the row does not jump when the charts mount in.
  const indicatorHeight = Math.round(mainHeight / 5);
  const reservedHeight =
    mainHeight + rsis.length * indicatorHeight + (rsis.length + 1) * 2;

  const chartChildren = [
    <CandleChart
      key="candle"
      txId={txId}
      live={live}
      mrcEpisode={mrcEpisode}
    />,
    ...rsis.map((rsi) => (
      <RsiPanel
        key={rsi.key}
        label={rsi.label}
        side={rsi.side}
        txId={txId}
        live={live}
      />
    )),
  ];

  return (
    <div ref={ref} style={{ width: '100%', minHeight: reservedHeight }}>
      {width > 0 && (
        <div className="nc-chart-fade-in">
          <MultiChart
            key={`${width}_${mainHeight}`}
            dimensions={{ width, height: mainHeight }}
          >
            {chartChildren}
          </MultiChart>
        </div>
      )}
    </div>
  );
};

export default ChartStack;
